import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../../../components/ui/Dialog';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';
import { useUsers } from '../../../core/hooks/useUsers';
import { useAuth } from '../../../core/contexts/AuthContext';
import { useClients } from '../../../core/hooks/useClients';
import { useOrganizations } from '../../../core/hooks/useOrganizations';
import type { DocumentSignature } from '../../../core/models/Document';
import { DocumentSignatureStatus } from '../../../core/models/Document';
import { findClientAccountForProject, resolveProjectContactEmail } from '../../../core/utils/projectContact';
import { Mail } from 'lucide-react';

interface SignatureRequestDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (signatures: DocumentSignature[]) => void;
  documentTitle: string;
}

export const SignatureRequestDialog: React.FC<SignatureRequestDialogProps> = ({
  open,
  onClose,
  onSubmit,
  documentTitle,
}) => {
  const { organizationId } = useAuth();
  const { users } = useUsers(organizationId);
  const { clients } = useClients(organizationId);
  const { organizations } = useOrganizations();
  const [signatures, setSignatures] = useState<DocumentSignature[]>([]);
  const [signingOrder, setSigningOrder] = useState<number>(1);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setValue,
    watch,
    reset,
  } = useForm({
    defaultValues: {
      signerType: 'user' as 'user' | 'client',
      userId: '',
      clientId: '',
      name: '',
      title: '',
      email: '',
      sendEmail: true,
    },
  });

  const signerType = watch('signerType');

  const handleAddSigner = (data: any) => {
    const newSignature: DocumentSignature = {
      userId: signerType === 'user' ? data.userId : data.clientId,
      userRole: signerType === 'user' ? 'organization' : 'client',
      status: DocumentSignatureStatus.PENDING,
      name: data.name,
      title: data.title,
      email: data.email,
    };

    setSignatures([...signatures, newSignature]);
    setSigningOrder(signingOrder + 1);
    reset();
  };

  const handleRemoveSigner = (index: number) => {
    setSignatures(signatures.filter((_, i) => i !== index));
  };

  const handleFinalSubmit = () => {
    onSubmit(signatures);
    setSignatures([]);
    setSigningOrder(1);
    reset();
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Request Signatures</DialogTitle>
          <DialogDescription>
            Add signers for "{documentTitle}". Signers will be notified via email.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Add Signer Form */}
          <form onSubmit={handleSubmit(handleAddSigner)} className="space-y-4 border-b pb-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Signer Type</label>
              <Select
                value={signerType}
                onValueChange={(value) => {
                  setValue('signerType', value as 'user' | 'client');
                  setValue('userId', '');
                  setValue('clientId', '');
                }}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="user">Client User</SelectItem>
                  <SelectItem value="client">Project</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {signerType === 'user' ? (
              <div className="space-y-2">
                <label className="text-sm font-medium">
                  User <span className="text-error">*</span>
                </label>
                <Select
                  onValueChange={(value) => {
                    setValue('userId', value);
                    const user = users.find((u) => u.uid === value);
                    if (user) {
                      setValue('name', user.displayName || user.email || '');
                      setValue('email', user.email || '');
                    }
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select a user" />
                  </SelectTrigger>
                  <SelectContent>
                    {users.map((user) => (
                      <SelectItem key={user.uid} value={user.uid}>
                        {user.displayName || user.email}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ) : (
              <div className="space-y-2">
                <label className="text-sm font-medium">
                  Project <span className="text-error">*</span>
                </label>
                <Select
                  onValueChange={(value) => {
                    setValue('clientId', value);
                    const client = clients.find((c) => c.id === value);
                    if (client) {
                      const clientAccount = findClientAccountForProject(client, organizations);
                      setValue('name', client.name);
                      setValue('email', resolveProjectContactEmail(client, clientAccount));
                    }
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select a project" />
                  </SelectTrigger>
                  <SelectContent>
                    {clients.map((client) => (
                      <SelectItem key={client.id} value={client.id}>
                        {client.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <Input
              label="Name"
              {...register('name', { required: 'Name is required' })}
              error={errors.name?.message}
            />

            <Input
              label="Title"
              {...register('title')}
              placeholder="e.g., CEO, Manager"
            />

            <Input
              label="Email"
              type="email"
              {...register('email', {
                required: 'Email is required',
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: 'Invalid email address',
                },
              })}
              error={errors.email?.message}
            />

            <div className="flex items-center space-x-2">
              <Checkbox
                {...register('sendEmail')}
                defaultChecked={true}
                onCheckedChange={(checked) => setValue('sendEmail', checked === true)}
              />
              <label className="text-sm font-medium">Send email notification</label>
            </div>

            <Button type="submit" size="sm">
              Add Signer
            </Button>
          </form>

          {/* Signers List */}
          {signatures.length > 0 && (
            <div className="space-y-2">
              <h3 className="font-semibold">Signers ({signatures.length})</h3>
              <div className="space-y-2">
                {signatures.map((signature, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between border rounded-lg p-3"
                  >
                    <div>
                      <p className="font-medium">{signature.name}</p>
                      {signature.title && (
                        <p className="text-sm text-gray-600">{signature.title}</p>
                      )}
                      {signature.email && (
                        <p className="text-xs text-gray-500">{signature.email}</p>
                      )}
                      <span className="text-xs text-gray-500">
                        Order: {index + 1} • {signature.userRole}
                      </span>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRemoveSigner(index)}
                    >
                      Remove
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={handleFinalSubmit}
            disabled={signatures.length === 0}
            loading={isSubmitting}
          >
            <Mail className="mr-2 h-4 w-4" />
            Send Signature Requests
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
